import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { fr, enUS } from "date-fns/locale";
import { format, addDays, subDays, isToday, isSameDay } from "date-fns";
import { findNextDayWithClasses } from "@/utils/scheduleNavigationUtils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRealtimeSchedule } from "@/hooks/useRealtimeSchedule";
import { motion } from "framer-motion";
import { Clock, Users, Check, X, ChevronLeft, ChevronRight, Loader2, Percent, User, Send, UserMinus } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

import { supabase } from "@/integrations/supabase/client";
import { getUserGymId } from '@/utils/gymAuth';
import { getDailyBookings } from '@/services/bookingsService';
import { getClients } from '@/services/clientsService';
import { trialProspectsApi } from '@/services/edgeFunctionWrappers/trialProspectsWrapper';


import { generateTimeSlots } from "@/utils/timeSlots";
import { scheduleSlotsHeigthMobile } from "@/utils/scheduleUtils";
import { getCategoryColor } from "@/utils/colorUtils";
import { getInitials } from "@/utils/commonUtils";
import { useScheduleSettings } from "@/hooks/useSlotDuration";

import type { ScheduledClass, AttendeeData } from "@/types/types";
import { useUserStore } from "@/store/userStore";
import { useAssignCoach, useUnassignCoach } from "@/hooks/useCoachAssignment";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


// -------------------------------------------------------------
// Helper types
// -------------------------------------------------------------
// Remplace la définition locale EnrichedClass par :
type EnrichedAttendee = AttendeeData & {
  assignedCoachId?: string | null;
  assignedCoachName?: string | null;
  assignedCoachPhoto?: string | null;
  isTrial?: boolean;
  trialNotes?: string | null;
  trialProspectId?: string;
};


interface EnrichedClass extends ScheduledClass {
  attendees: EnrichedAttendee[];
  booked_count: number;
}


// -------------------------------------------------------------
// Data layer (React Query)
// -------------------------------------------------------------
const fetchDailyClasses = async (dateISO: string, calendarId?: string | null) => {
  const gymId = getUserGymId();
  
  let query = supabase
    .from("scheduled_classes")
    .select("id, name, instructor, time, duration, category_id, course_id, class_date, capacity, gym_id, calendar_id")
    .eq("class_date", dateISO)
    .eq("gym_id", gymId) as any;

  if (calendarId) {
    query = query.eq("calendar_id", calendarId);
  }

  const { data: classes, error } = await query.order("time", { ascending: true }) as { data: any[] | null; error: any };
  if (error) throw error;

  const classIds = (classes ?? []).map((c) => c.id);
  if (!classIds.length) return [] as EnrichedClass[];

  // ✅ Utiliser getDailyBookings pour récupérer TOUS les bookings filtrés par role='client'
  const bookings = await getDailyBookings(dateISO);
  console.log('[fetchDailyClasses] Fetched daily bookings (filtered by role=client):', bookings.length);


  const bookingIds = (bookings ?? []).map((b) => b.bookingId);
  let assignments: Array<{ id: string; class_booking_id: string; coach_id: string }> = [];
  let coachProfiles: Array<{ id: string; first_name: string | null; last_name: string | null; profile_photo_url: string | null; }> = [];

  if (bookingIds.length) {
    const { data: ass, error: aErr } = await supabase
      .from("class_booking_coach")
      .select("id, class_booking_id, coach_id")
      .in("class_booking_id", bookingIds);
    if (aErr) throw aErr;
    assignments = ass ?? [];

    const coachIds = Array.from(new Set(assignments.map((a) => a.coach_id)));
    if (coachIds.length) {
      const { data: cps, error: cErr } = await supabase
        .from("user_profiles")
        .select("id, first_name, last_name, profile_photo_url")
        .in("id", coachIds);
      if (cErr) throw cErr;
      coachProfiles = cps ?? [];
    }
  }

  const assignmentByBooking = new Map<string, { coach_id: string }>();
  assignments.forEach(a => assignmentByBooking.set(a.class_booking_id, { coach_id: a.coach_id }));


  const userIds = Array.from(new Set((bookings ?? []).map((b) => b.userId)));
  let profiles: Array<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    profile_photo_url: string | null;
    clothing_size: string | null;
  }> = [];
  if (userIds.length) {
    // ✅ Utiliser getClients pour récupérer uniquement les profils des clients
    const clientProfiles = await getClients(gymId, userIds);
    profiles = (clientProfiles ?? []).map((c: any) => ({
      id: c.id,
      first_name: c.firstName,
      last_name: c.lastName,
      phone: c.phone,
      profile_photo_url: c.profilePhoto,
      clothing_size: c.clothingSize,
    }));
    console.log('[fetchDailyClasses] Fetched client profiles:', profiles.length);
  }

  const attendeesByClass = new Map<string, AttendeeData[]>();
  (bookings ?? []).forEach((b) => {
    const up = profiles.find((p) => p.id === b.userId);
    const ass = assignmentByBooking.get(b.bookingId);
    const coachP = ass ? coachProfiles.find(p => p.id === ass.coach_id) : undefined; 
    const attendee: EnrichedAttendee = {
      id: b.bookingId,
      userId: b.userId,
      userFirstName: up?.first_name ?? "",
      userLastName: up?.last_name ?? "",
      phone: up?.phone ?? null,
      profilePhoto: up?.profile_photo_url ?? null,
      attended: false,
      notes: null,
      status: b.status ?? null,
      clothingSize: up?.clothing_size ?? null,
      assignedCoachId: ass?.coach_id ?? null,
      assignedCoachName: coachP ? `${coachP.first_name ?? ""} ${coachP.last_name ?? ""}`.trim() : null,
      assignedCoachPhoto: coachP?.profile_photo_url ?? null,
    };
  
    const arr = attendeesByClass.get(b.classId) ?? [];
    arr.push(attendee);
    attendeesByClass.set(b.classId, arr);
  });

  // ✅ Trial prospects (séances d'essai) — read-only for coach, with badge
  try {
    if (gymId && classIds.length) {
      const trials = await trialProspectsApi.listByClasses(gymId, classIds, dateISO);
      (trials ?? []).forEach((t: any) => {
        const trialAttendee: EnrichedAttendee = {
          id: `trial-${t.id}`,
          userId: `trial-${t.id}`,
          userFirstName: t.first_name ?? '',
          userLastName: t.last_name ?? '',
          phone: t.phone ?? null,
          profilePhoto: null,
          attended: false,
          notes: null,
          status: t.status ?? null,
          clothingSize: t.clothing_size ?? null,
           assignedCoachId: t.coach_id ?? null,
           assignedCoachName: t.coach_id ? `${t.coach_first_name ?? ''} ${t.coach_last_name ?? ''}`.trim() : null,
          assignedCoachPhoto: null,
          isTrial: true,
          trialNotes: t.notes ?? null,
           trialProspectId: t.id,
        };
        const arr = attendeesByClass.get(t.class_id) ?? [];
        arr.push(trialAttendee);
        attendeesByClass.set(t.class_id, arr);
      });
    }
  } catch (e) {
    console.warn('[fetchDailyClasses] trial prospects fetch failed (non-blocking)', e);
  }



  const enriched: EnrichedClass[] = (classes ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    instructor: c.instructor,
    time: c.time,
    duration: c.duration ?? "45",
    category: "",
    categoryId: c.category_id ?? undefined,
    courseId: c.course_id ?? undefined,
    class_date: c.class_date,
    capacity: c.capacity,
    attendance: 0,
    spots_available: undefined,
    booked_count: attendeesByClass.get(c.id)?.length ?? 0,
    attendees: attendeesByClass.get(c.id) ?? [],
    calendar_id: c.calendar_id ?? undefined,
  }));

  return enriched.sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00"));
};

const useCoachDailyData = (date: Date, calendarId?: string | null) => {
  const dateISO = format(date, "yyyy-MM-dd");
  return useQuery({
    queryKey: ["coach-daily", dateISO, calendarId ?? "all"],
    queryFn: () => fetchDailyClasses(dateISO, calendarId),
  });
};

// -------------------------------------------------------------
// Notifications (per user)
// -------------------------------------------------------------
import { createNotification } from "@/services/edgeFunctionWrappers/notificationsManagementWrapper";

const useNotifyAdherent = () => {
  return useMutation({
    mutationFn: async (payload: { classId: string; userId: string; message: string; when?: Date }) => {
      const { error } = await createNotification({
        targetUserId: payload.userId,
        type: "COACH_BROADCAST",
        payload: {
          class_id: payload.classId,
          message: payload.message,
        },
        scheduledAt: (payload.when ?? new Date()).toISOString(),
        gymId: getUserGymId(),
      });
      if (error) throw error;
      return true;
    },
  });
};

// -------------------------------------------------------------
// UI Components
// -------------------------------------------------------------
function TopSummary({ classes }: { classes: EnrichedClass[] }) {
  const { t } = useTranslation("adminui");
  const totalCourses = classes.length;
  const totalParticipants = classes.reduce((s, c) => s + (c.booked_count || 0), 0);
  const totalCapacity = classes.reduce((s, c) => s + (c.capacity || 0), 0);
  const fill = totalCapacity ? Math.round((totalParticipants / totalCapacity) * 100) : 0;

  return (
    <div className="coach-day-summary grid grid-cols-3 gap-2">
      <Card>
        <CardContent className="flex items-center gap-2 p-3">
          <span className="coach-icon-box flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-primary"><Clock className="h-4 w-4" /></span>
          <div className="text-xs">
            <div className="text-base font-bold">{totalCourses}</div>
            <div className="text-muted-foreground">{t("coachSchedule.courses")}</div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex items-center gap-2 p-3">
          <span className="coach-icon-box flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-primary"><Users className="h-4 w-4" /></span>
          <div className="text-xs">
            <div className="text-base font-bold">{totalParticipants}</div>
            <div className="text-muted-foreground">{t("coachSchedule.participants")}</div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex items-center gap-2 p-3">
          <span className="coach-icon-box flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-primary"><Percent className="h-4 w-4" /></span>
          <div className="text-xs">
            <div className="text-base font-bold">{fill}%</div>
            <div className="text-muted-foreground">{t("coachSchedule.fillRate")}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PerUserNotifyDialog({
  open,
  onOpenChange,
  classId,
  userId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  classId: string;
  userId: string | null;
}) {
  const { t } = useTranslation("adminui");
  const [message, setMessage] = useState("");
  const { toast } = useToast();
  const notify = useNotifyAdherent();

  const onSend = async () => {
    if (!userId) return;
    try {
      await notify.mutateAsync({ classId, userId, message });
      toast({ title: t("coachSchedule.messageScheduled"), description: t("coachSchedule.memberNotifiedIndividually") });
      setMessage("");
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: t("coachSchedule.sendError"), description: e?.message ?? t("coachSchedule.checkRlsRights"), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>{t("coachSchedule.notifyMember")}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("coachSchedule.notifyPlaceholder")}
        />
        <DialogFooter>
          <Button onClick={onSend} disabled={!message.trim()}>
            <Send className="h-4 w-4 mr-2" /> {t("coachSchedule.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttendeesSheet({
  open,
  onOpenChange,
  klass,
  onAssign,
  onUnassign,
  isCoach,
  currentUserId,
  isAdmin,
  assignPending, // ✅ nouveau
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  klass: EnrichedClass | null;
  onAssign: (attendee: EnrichedAttendee) => void;
  onUnassign: (attendee: EnrichedAttendee) => void;
  isCoach: boolean;
  currentUserId?: string | null;
  isAdmin: boolean;
  assignPending?: boolean;                        // ✅ nouveau
}) {
  const { t } = useTranslation("adminui");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyUserId, setNotifyUserId] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState<{ attendee: EnrichedAttendee; attendeeName: string } | null>(null);

  const requestAssign = (attendee: EnrichedAttendee, attendeeName: string) => {
    setPending({ attendee, attendeeName });
    setConfirmOpen(true);
  };

  const onConfirmAssign = async () => {
    if (!pending) return;
    await onAssign(pending.attendee);
    setConfirmOpen(false);
    setPending(null);
  };


  if (!klass) return null;
  const title = `${klass.name} · ${klass.time} (${klass.duration}m)`;
  const classStarted = new Date() >= new Date(`${klass.class_date}T${klass.time}:00`);

  const openNotifyFor = (userId: string) => {
    setNotifyUserId(userId);
    setNotifyOpen(true);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl h-[75vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left">{title}</SheetTitle>
          <SheetDescription className="text-left">
            {t("coachSchedule.enrolledCount", { booked: klass.booked_count, capacity: klass.capacity })}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {klass.attendees.length === 0 && (
            <div className="text-sm text-muted-foreground">{t("coachSchedule.noEnrolled")}</div>
          )}

          {klass.attendees.map((a) => (
            <Card key={a.id} className="rounded-2xl">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <Avatar className="h-8 w-8 shrink-0">
                      {a.profilePhoto ? (
                        <AvatarImage src={a.profilePhoto} />
                      ) : (
                        <AvatarFallback>
                          {getInitials(`${a.userFirstName} ${a.userLastName}`) || <User className="h-4 w-4" />}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="text-sm min-w-0 flex-1">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <span className="truncate">{a.userFirstName} {a.userLastName}</span>
                        {a.isTrial && (
                          <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wide">
                            {t("coachSchedule.trialSession")}
                          </Badge>
                        )}
                      </div>
                      {a.clothingSize && <div className="text-muted-foreground text-xs">{a.clothingSize}</div>}
                      {a.isTrial && a.phone && (
                        <a
                          href={`tel:${a.phone}`}
                          className="text-xs text-primary underline-offset-2 hover:underline block mt-0.5"
                        >
                          📞 {a.phone}
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!a.isTrial && isAdmin && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8"
                        onClick={() => openNotifyFor(a.userId)}
                        title={t("coachSchedule.notifyThisMember")}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    )}

                    {a.assignedCoachId ? (
                      <Badge variant="default" className="rounded-full">
                        {a.assignedCoachId === currentUserId
                          ? t("coachSchedule.coachAssignedYou")
                          : t("coachSchedule.coachAssignedName", { name: a.assignedCoachName || "" })}
                      </Badge>
                    ) : null}
                    {isCoach && classStarted && a.assignedCoachId === currentUserId && (
                      <Button size="sm" variant="outline" onClick={() => onUnassign(a)} disabled={assignPending}>
                        <UserMinus className="mr-1 h-3.5 w-3.5" />{t("coachSchedule.removeMe")}
                      </Button>
                    )}
                    {isCoach && classStarted && !a.assignedCoachId && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => requestAssign(a, `${a.userFirstName} ${a.userLastName}`.trim())}
                        >
                          {t("coachSchedule.assignToMe")}
                        </Button>
                    )}
                  </div>
                </div>

                {a.isTrial && a.trialNotes && (
                  <div className="rounded-lg bg-muted/50 border border-border/50 p-2 text-xs whitespace-pre-wrap">
                    <div className="font-semibold text-muted-foreground mb-0.5">{t("coachSchedule.prospectNote")}</div>
                    <div>{a.trialNotes}</div>
                  </div>
                )}
                {isCoach && !classStarted && (
                  <div className="text-xs text-muted-foreground">{t("coachSchedule.availableAtStart")}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <SheetFooter className="mt-6" />
      </SheetContent>

      <PerUserNotifyDialog open={notifyOpen} onOpenChange={setNotifyOpen} classId={klass.id} userId={notifyUserId} />
    
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("coachSchedule.confirmAssignment")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("coachSchedule.assignConfirmPrefix")}
              <span className="font-medium"> {pending?.attendeeName}</span>
              {klass?.name ? <> {t("coachSchedule.assignConfirmForSession")} <span className="font-medium">{klass.name}</span></> : null}
              {klass?.time || klass?.class_date ? (
                <> — {klass?.time}{klass?.class_date ? ` · ${format(new Date(klass.class_date), "dd MMM yyyy")}` : ""}</>
              ) : null}
              {t("coachSchedule.assignConfirmSuffix")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={assignPending}>{t("coachSchedule.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmAssign} disabled={assignPending}>
              {assignPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("coachSchedule.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </Sheet>
  );
}

function computeOverlapGroups(classes: EnrichedClass[]): Map<string, { columnIndex: number; totalColumns: number }> {
  const result = new Map<string, { columnIndex: number; totalColumns: number }>();
  if (classes.length === 0) return result;

  const withCalendar = classes.filter(c => c.calendar_id);
  const withoutCalendar = classes.filter(c => !c.calendar_id);

  const calendarIds = [...new Set(withCalendar.map(c => c.calendar_id!))];
  const calendarToCol = new Map<string, number>();
  calendarIds.forEach((id, idx) => calendarToCol.set(id, idx));
  const totalColumns = Math.max(calendarIds.length, 1);

  for (const klass of withCalendar) {
    result.set(klass.id, {
      columnIndex: calendarToCol.get(klass.calendar_id!)!,
      totalColumns,
    });
  }

  for (const klass of withoutCalendar) {
    result.set(klass.id, { columnIndex: 0, totalColumns: 1 });
  }

  return result;
}

function ClassCard({
  klass,
  onOpenAttendees,
  slotDuration,
  startTime,
  columnIndex = 0,
  totalColumns = 1,
}: {
  klass: EnrichedClass;
  onOpenAttendees: (klass: EnrichedClass) => void;
  slotDuration: number;
  startTime: string;
  columnIndex?: number;
  totalColumns?: number;
}) {
  const rowH = scheduleSlotsHeigthMobile;
  const [h, m] = (klass.time || "08:00").split(':').map(Number);
  const [bh, bm] = startTime.split(':').map(Number);
  const minutesSinceBase = (h * 60 + m) - (bh * 60 + bm);
  const durationMin = parseInt(klass.duration || "45");
  const pxPerMinute = rowH / slotDuration;
  const top = minutesSinceBase * pxPerMinute;
  const height = Math.max(rowH, durationMin * pxPerMinute - 8);
  const colorClasses = getCategoryColor(klass.category || "");
  const isFull = klass.booked_count >= (klass.capacity ?? 0);

  const widthPercent = 100 / totalColumns;
  const leftPercent = columnIndex * widthPercent;
  const isCompact = totalColumns > 1;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        top,
        height,
        left: `calc(${leftPercent}% + 4px)`,
        width: `calc(${widthPercent}% - 8px)`,
      }}
      className={cn(
        `coach-day-class absolute rounded-md border shadow-sm flex flex-col justify-between`,
        colorClasses,
        isCompact ? 'p-2' : 'p-3'
      )}
      onClick={() => onOpenAttendees(klass)}
      role="button"
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className={cn("font-semibold leading-tight truncate", isCompact ? "text-xs" : "text-sm")}>{klass.name}</div>
          <div className="text-xs opacity-90 flex items-center gap-1">
            <Clock className="h-3 w-3 shrink-0" /> {klass.time} · {klass.duration}m
          </div>
        </div>
        <Badge className="coach-day-capacity shrink-0 rounded-full" variant={isFull ? "default" : "secondary"}>
          <Users className="mr-1 h-3 w-3" />
          {klass.booked_count}/{klass.capacity}
        </Badge>
      </div>
    </motion.div>
  );
}

// -------------------------------------------------------------
// Props pour la sync avec le parent (initialDate / onDateChange)
// -------------------------------------------------------------
interface CoachDailyScheduleProps {
  initialDate?: Date;
  onDateChange?: (date: Date) => void;
  selectedCalendarId?: string | null;
}

// -------------------------------------------------------------
// Main exported component
// -------------------------------------------------------------
export default function CoachDailySchedule({
  initialDate,
  onDateChange,
  selectedCalendarId,
}: CoachDailyScheduleProps) {
  const { i18n, t } = useTranslation("adminui");
  const { role, user } = useUserStore();
  const { toast } = useToast();

  // init avec initialDate si fournie
  const [date, setDate] = useState<Date>(initialDate ?? new Date());
  const [openSheet, setOpenSheet] = useState(false);
  const [selectedClass, setSelectedClass] = useState<EnrichedClass | null>(null);
  const [isAutoNavigating, setIsAutoNavigating] = useState(false);
  // Dernière date (ISO) depuis laquelle une recherche automatique a DÉJÀ été
  // tentée. Sans cette mémoire, une recherche infructueuse (aucun cours dans
  // les 7 jours) remettait `isAutoNavigating` à false dans son `finally`, ce
  // qui re-déclenchait l'effet sur la même date vide → boucle infinie de
  // recherches et de toasts « Planning vide ». Une date n'est cherchée qu'une
  // fois ; naviguer vers une autre date réarme la recherche.
  const lastAutoSearchFromRef = React.useRef<string | null>(null);

  const { data, isLoading, isError, refetch } = useCoachDailyData(date, selectedCalendarId);
  const gymId = useUserStore((state) => state.gymId);
  const queryClient = useQueryClient();

  const dateISO = format(date, 'yyyy-MM-dd');
  const invalidateCoachDaily = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['coach-daily', dateISO] });
    queryClient.refetchQueries({ queryKey: ['coach-daily', dateISO], type: 'active' });
  }, [queryClient, dateISO]);

  // ✅ Realtime: refresh quand cours / réservations changent pour ce gym + ce jour
  useRealtimeSchedule({
    enabled: !!gymId && !!user?.id,
    gymId: gymId || '',
    startDate: date,
    endDate: date,
    onScheduleChange: invalidateCoachDaily,
    onBookingChange: invalidateCoachDaily,
    showNotifications: false,
  });

  // ✅ Realtime: assignations coach (un autre coach s'attribue un adhérent)
  useEffect(() => {
    if (!gymId) return;
    const ch = supabase
      .channel(`coach-assignments-${gymId}-${dateISO}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'class_booking_coach' },
        () => invalidateCoachDaily()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [gymId, dateISO, invalidateCoachDaily]);

  // ✅ Realtime: séances d'essai (prospects) — refresh badge en direct
  useEffect(() => {
    if (!gymId) return;
    const ch = supabase
      .channel(`coach-trials-${gymId}-${dateISO}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trial_prospects', filter: `gym_id=eq.${gymId}` },
        () => invalidateCoachDaily()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [gymId, dateISO, invalidateCoachDaily]);


  const locale = i18n.language === "fr" ? fr : enUS;
  const dateLabel = useMemo(() => {
    const base = format(date, "EEEE d MMMM", { locale });
    return isToday(date) ? `${base} · Aujourd'hui` : base;
  }, [date, locale]);

  useEffect(() => {
    refetch();
  }, [user?.id, refetch]);

  // sync si initialDate change côté parent
  useEffect(() => {
    if (initialDate && !isSameDay(initialDate, date)) {
      setDate(initialDate);
    }
  }, [initialDate, date]);

  // Navigation automatique vers le prochain jour avec cours
  useEffect(() => {
    // Conditions pour déclencher la recherche :
    // 1. Pas en chargement
    // 2. Pas d'erreur
    // 3. Aucun cours trouvé
    // 4. Pas déjà en train de naviguer automatiquement
    // 5. Aucune recherche déjà tentée depuis CETTE date (sinon une recherche
    //    infructueuse repartait en boucle dès que isAutoNavigating retombait)
    // La clé inclut le planning filtré : changer de planning sur une date déjà
    // cherchée doit pouvoir relancer une recherche.
    const searchKey = `${dateISO}:${selectedCalendarId ?? 'all'}`;
    if (
      !isLoading &&
      !isError &&
      (data?.length ?? 0) === 0 &&
      !isAutoNavigating &&
      lastAutoSearchFromRef.current !== searchKey
    ) {
      lastAutoSearchFromRef.current = searchKey;
      setIsAutoNavigating(true);
      
      // Le filtre de planning est propagé à la recherche : sans lui, filtré
      // sur « Cours Privés », on sautait vers un jour n'ayant que des cours
      // d'un autre planning — affiché vide, ce qui relançait un saut, etc.
      findNextDayWithClasses(date, 7, (d) => fetchDailyClasses(d, selectedCalendarId))
        .then((result) => {
          if (result) {
            const { date: nextDate, daysSkipped } = result;
            
            // Toast informatif
            toast({
              title: "Jour sans cours",
              description: t("coachSchedule.noCoursePassTo", { day: format(date, "EEEE d MMMM", { locale }), nextDay: format(nextDate, "EEEE d MMMM", { locale }), count: daysSkipped }),
              duration: 3000,
            });
            
            // Navigation automatique
            setDate(nextDate);
            onDateChange?.(nextDate);
          } else {
            // Aucun cours trouvé dans les 7 prochains jours
            toast({
              title: t("coachSchedule.emptySchedule"),
              description: t("coachSchedule.noCourseNext7Days"),
              variant: "destructive",
              duration: 4000,
            });
          }
        })
        .catch((error) => {
          console.error("Erreur lors de la recherche automatique:", error);
        })
        .finally(() => {
          setIsAutoNavigating(false);
        });
    }
  }, [isLoading, isError, data, date, dateISO, selectedCalendarId, isAutoNavigating, onDateChange, toast, locale]);

  // Réinitialiser isAutoNavigating quand la date change manuellement
  useEffect(() => {
    setIsAutoNavigating(false);
  }, [date]);

  // navigation jour précédent / suivant + callback au parent
  const onPrev = () =>
    setDate((d) => {
      const nd = subDays(d, 1);
      onDateChange?.(nd);
      return nd;
    });

  const onNext = () =>
    setDate((d) => {
      const nd = addDays(d, 1);
      onDateChange?.(nd);
      return nd;
    });

  const onOpenAttendees = (klass: EnrichedClass) => {
    setSelectedClass(klass);
    setOpenSheet(true);
  };

  const isCoach = role === "coach";
  const coachId = user?.id;
  const assignCoach = useAssignCoach(coachId);
  const unassignCoach = useUnassignCoach(coachId);
  const [trialActionPending, setTrialActionPending] = useState(false);

  const handleAssign = async (attendee: EnrichedAttendee) => {
    try {
      if (!gymId || !coachId) throw new Error(t("coachSchedule.cannotAssignSession"));
      if (attendee.isTrial) {
        if (!attendee.trialProspectId) throw new Error(t("coachSchedule.cannotAssignSession"));
        setTrialActionPending(true);
        await trialProspectsApi.assignCoach(gymId, attendee.trialProspectId, coachId);
      } else {
        await assignCoach.mutateAsync(attendee.id);
      }

      // Optimistic UI dans le drawer ouvert
      setSelectedClass((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          attendees: prev.attendees.map((a) =>
            a.id === attendee.id
              ? {
                  ...a,
                  assignedCoachId: coachId ?? null,
                  // On peut afficher "Vous" directement; un refetch remplira le nom complet
                  assignedCoachName: a.assignedCoachName ?? undefined,
                }
              : a
          ),
        };
      });

      // Optionnel: refetch pour consolider noms/photos
      await refetch();

      toast({ title: t("coachSchedule.assignmentSaved") });
    } catch (e: any) {
      toast({
        title: t("coachSchedule.error"),
        description: e?.message ?? t("coachSchedule.cannotAssignSession"),
        variant: "destructive",
      });
    } finally { setTrialActionPending(false); }
  };

  const handleUnassign = async (attendee: EnrichedAttendee) => {
    try {
      if (!gymId || !coachId) throw new Error(t("coachSchedule.cannotAssignSession"));
      if (attendee.isTrial) {
        if (!attendee.trialProspectId) throw new Error(t("coachSchedule.cannotAssignSession"));
        setTrialActionPending(true);
        await trialProspectsApi.unassignCoach(gymId, attendee.trialProspectId);
      } else {
        await unassignCoach.mutateAsync(attendee.id);
      }
      setSelectedClass((prev) => prev ? { ...prev, attendees: prev.attendees.map((item) => item.id === attendee.id ? { ...item, assignedCoachId: null, assignedCoachName: null } : item) } : prev);
      await refetch();
      toast({ title: t("coachSchedule.assignmentRemoved") });
    } catch (e: any) {
      toast({ title: t("coachSchedule.error"), description: e?.message ?? t("coachSchedule.cannotAssignSession"), variant: "destructive" });
    } finally { setTrialActionPending(false); }
  };

  const { slotDuration, startTime, endTime } = useScheduleSettings();
  const timeSlots = generateTimeSlots(slotDuration, startTime, endTime);
  const gridHeight = timeSlots.length * scheduleSlotsHeigthMobile;

  // Tic d'horloge à la minute : sans lui, la ligne « maintenant » restait
  // figée à sa position d'ouverture de la page (et n'apparaissait jamais si
  // la page était ouverte avant les heures d'ouverture de la salle).
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!isToday(date)) return;
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [date]);

  // Repère « maintenant » sur la timeline (aujourd'hui uniquement) : position
  // en pixels de l'heure courante, même calcul que les cartes de cours.
  const nowOffsetPx = useMemo(() => {
    if (!isToday(date)) return null;
    const now = new Date();
    const [bh, bm] = startTime.split(":").map(Number);
    const minutesSinceBase = now.getHours() * 60 + now.getMinutes() - (bh * 60 + bm);
    const px = minutesSinceBase * (scheduleSlotsHeigthMobile / slotDuration);
    if (px < 0 || px > gridHeight) return null; // hors plage d'ouverture
    return px;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, startTime, slotDuration, gridHeight, nowTick]);

  // Auto-scroll : centrer la vue sur le repère « maintenant » — donc sur le
  // créneau immédiatement inférieur à l'heure courante — une fois par jour
  // affiché, quand les cours sont chargés.
  const nowLineRef = useRef<HTMLDivElement | null>(null);
  const autoScrolledForRef = useRef<string | null>(null);
  useEffect(() => {
    if (nowOffsetPx == null || isLoading) return;
    if (autoScrolledForRef.current === dateISO) return;
    const el = nowLineRef.current;
    if (!el) return;
    autoScrolledForRef.current = dateISO;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [nowOffsetPx, isLoading, dateISO]);

  return (
    <div className="coach-day-view mx-auto max-w-3xl p-0 pb-24">
      {/* Header sticky */}
      <div className="coach-day-sticky sticky top-0 z-20 bg-background/90 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="coach-day-date-nav flex items-center justify-between rounded-md border px-2 py-2">
          <Button variant="ghost" size="icon" onClick={onPrev} className="rounded-full">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <div className="text-xs text-muted-foreground">{format(date, "yyyy-MM-dd")}</div>
            <div className="font-semibold">{dateLabel}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onNext} className="rounded-full">
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        <div className="mt-3">
          <TopSummary classes={data ?? []} />
        </div>
      </div>

      {(isLoading || isAutoNavigating) && (
        <div className="h-[60vh] flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" /> 
          {isAutoNavigating ? t("coachSchedule.searchingNextDay") : t("coachSchedule.loadingSchedule")}
        </div>
      )}

      {isError && <div className="text-sm text-red-500">{t("coachSchedule.loadErrorConnection")}</div>}

      {!isLoading && !isAutoNavigating && (data?.length ?? 0) === 0 && (
        <div className="text-sm text-muted-foreground mt-6">
          {t("coachSchedule.noCourseScheduledForDate")}
          <br />
          <span className="text-xs">{t("coachSchedule.searchingNextAvailableDay")}</span>
        </div>
      )}

      {/* Timeline grid */}
      <div className="coach-day-timeline relative mt-4 overflow-hidden rounded-md border">
        {/* Times gutter */}
        <div className="absolute left-0 top-0 bottom-0 w-14 bg-muted/40 border-r">
          {timeSlots.map((t) => (
            <div
              key={t}
              className="text-[10px] px-1 flex items-start justify-end pr-2 text-muted-foreground"
              style={{ borderBottom: "1px dashed hsl(var(--muted-foreground)/0.2)", height: scheduleSlotsHeigthMobile }}
            >
              {t}
            </div>
          ))}
        </div>

        {/* Grid area */}
        <div className="ml-14 relative" style={{ height: gridHeight }}>
          {/* Ligne « maintenant » (aujourd'hui, pendant les heures d'ouverture) */}
          {nowOffsetPx != null && (
            <div
              ref={nowLineRef}
              className="pointer-events-none absolute inset-x-0 z-10"
              style={{ top: nowOffsetPx }}
              aria-hidden
            >
              <div className="relative border-t-2 border-red-500/80">
                <span className="absolute -left-1.5 -top-[5px] h-2 w-2 rounded-full bg-red-500" />
              </div>
            </div>
          )}
          {(() => {
            const classes = data ?? [];
            const overlapMap = computeOverlapGroups(classes);
            return classes.map((klass) => {
              const info = overlapMap.get(klass.id) ?? { columnIndex: 0, totalColumns: 1 };
              return (
                <ClassCard
                  key={klass.id}
                  klass={klass}
                  onOpenAttendees={onOpenAttendees}
                  slotDuration={slotDuration}
                  startTime={startTime}
                  columnIndex={info.columnIndex}
                  totalColumns={info.totalColumns}
                />
              );
            });
          })()}
        </div>
      </div>

      {/* Attendees Drawer */}
      <AttendeesSheet
        open={openSheet}
        onOpenChange={setOpenSheet}
        klass={selectedClass}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
        isCoach={isCoach}
        currentUserId={user?.id}
        isAdmin={useUserStore.getState().role === "admin"}
        assignPending={assignCoach.isPending || unassignCoach.isPending || trialActionPending}
      />


    </div>
  );
}
